/**
 * hint-category-extractor.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { ClassProvider, Injectable } from "@angular/core";
import { MessageHintName, StepPresentationHints }
    from "../../../common/data/presentation-hints";
import { AlgorithmStep, IntakeStep, ReportStep } from "../../structure/";

import { StepTransformer, TransformerEffects }
    from "./step-transformer";

// TODO: Really the whole SelfParameterizing angle is pretty stupid; the maps
// should just take hints OR functions as their values, rather than trying to
// mix the function into the hint type definition! (I was experimenting with
// some TS stuff at the time so I had generics manipulation on the brain...)

/**
 * A type for objects that can transform themselves with an optional _evaluate
 * function and a parameter string.
 */
type SelfParameterizing<T> = T & {
    _evaluate? : (obj : T, param : string) => T;
};

/**
 * Create an anonymous function that takes a parameter and assigns it to a
 * particular property of an object, optionally converting it somehow.
 */
function assignProp<T> (
    propName : string,
    convert : (p : string) => any = ((_ : any) => _)
) : (t : T, param : string) => T
{
    return (t : T, param : string) => {
        t[propName] = convert(param);
        return t;
    };
}

/**
 * Create a {@link StepPresentationHints} that assigns a parameterized hint
 * using a given value converter.
 */
function valueGetter (
    propName : string,
    convert : (p : string) => any = ((_ : any) => _)
) : StepPresentationHints
{
    return {_evaluate: assignProp<StepPresentationHints>(propName, convert)};
}

/**
 * A map from category strings to the hints that should be applied to them,
 * for {@link IntakeStep}s.
 */
const INTAKE_CONVERSION_TABLE :
    Map<MessageHintName, SelfParameterizing<StepPresentationHints>> =
    new Map<MessageHintName, SelfParameterizing<StepPresentationHints>>([
        ["hint-mandatory", {mandatory: true}],
        ["hint-multiline", {multiline: true}],
        // ["hint-currency", {currency: true}],
        // ["hint-notification-template", {"notification-template": {now: 0}}],
        ["hint-developer", {developer: true}],
        ["hint-autofill-console-history",
            {autofill: "console-history", editable: false}],
        ["hint-autofill-message-history",
            {autofill: "message-history", editable: false}],
        ["hint-autofill-navigation-history",
            {autofill: "navigation-history", editable: false}],
        ["hint-autofill-stack-trace",
            {autofill: "stack-trace", editable: false}],
        ["hint-autofill-user-agent", {autofill: "user-agent", editable: false}],
        ["hint-autofill-now", {autofill: "now"}],
        ["hint-autofill-today", {autofill: "today"}],
        ["hint-autofill-user-name", {autofill: "user-name", editable: false}],
        ["hint-autofill-organization",
            {autofill: "organization", editable: false}],
        ["hint-autofill-window-size", {autofill: "window-size"}],
        ["hint-autofill-screen-size", {autofill: "screen-size"}],
        ["hint-autofill-pixel-ratio", {autofill: "pixel-ratio"}],
        ["hint-table", valueGetter("table")],
        ["hint-column", valueGetter("column")],
        ["hint-comment-column", {commentColumn: true}],
        ["hint-editable", valueGetter(
            "editable", (v : string) => v.toLowerCase() === "true")],
        // ["hint-quick-link", valueGetter("quicklink")],
        ["hint-quick-link-bottom", valueGetter("quicklink")],
        // ["hint-control-visible", valueGetter(
        //     "controlVisible", (v : string) => v.toLowerCase() === "true")],
        // ["hint-display-mode", valueGetter("displayMode")],
        ["hint-table-sort-action", valueGetter("tableSorter")],
        ["hint-highlight-inequality", valueGetter("highlightDifferenceWith")],
        ["hint-boolean-submit-button", {useSubmitWithValue: true}],
        ["hint-special-presentation", valueGetter("specialPresentation")]
    ] as Array<[MessageHintName, SelfParameterizing<StepPresentationHints>]>);

/**
 * A map from category strings to the hints that should be applied to them,
 * for {@link ReportStep}s.
 */
const REPORT_CONVERSION_TABLE :
    Map<MessageHintName, SelfParameterizing<StepPresentationHints>> =
    new Map<MessageHintName, SelfParameterizing<StepPresentationHints>>([
        ["hint-developer", {developer: true}],
        ["hint-inline", {inline: true}],
        ["hint-initially-expanded", {expanded: true}],
        ["hint-alert", {alert: true}]
    ] as Array<[MessageHintName, SelfParameterizing<StepPresentationHints>]>);

/**
 * The {@link HintCategoryExtractor} stage of the {@link AlgorithmStep}
 * pipeline extracts presentation hints from a step's classifications. This is
 * effectively a polyfill until codified presentation hints are added to
 * the Hammurabi language / the client-server protocol.
 */
@Injectable()
export class HintCategoryExtractor extends StepTransformer
{
    // Where this class should be sorted in the transformer list
    public order : number = -10;

    public matchesType (type : any) : boolean
    {
        return type === IntakeStep || type === ReportStep;
    }

    /**
     * Extract and apply any hints present in the {@link AlgorithmStep}'s
     * list of classifications.
     */
    public create (step : AlgorithmStep) : TransformerEffects
    {
        step.classifications = step.classifications || [];
        step.hints = step.hints || {};

        // Use the right table for this step type
        const table : Map<string, SelfParameterizing<StepPresentationHints>> = (
            (step instanceof IntakeStep)
                ? INTAKE_CONVERSION_TABLE
                : REPORT_CONVERSION_TABLE);

        step.classifications.forEach((category : string) => {
            const [label, value] : string[] = category.split("=");
            let hint : SelfParameterizing<StepPresentationHints> =
                table.get(label);
            if (hint)
            {
                if (hint._evaluate)
                {
                    // if a hint-transforming function
                    hint = Object.assign({}, hint._evaluate(hint, value));
                    delete hint._evaluate;

                    if ((step as IntakeStep).type === "boolean")
                    {
                        // Mark booleans using parameterizers as checkbox-style
                        // TODO: This is a VERY STUPID and INCORRECT semantic
                        // association, but right now only tabular questions
                        // use _evaluate functions, so it's an easy place to
                        // dump the code.
                        hint.checkbox = true;
                    }
                }
                // Assign the inferred hint(s) to the step
                Object.assign(step.hints, hint);
            }
        });
        return {};
    }

    public apply () : TransformerEffects
    {
        return {};
    }
}

/**
 * Create a provider for easy import that uses the {@link StepTransformer} class
 * as a multi-provider injection token.
 */
export const HINT_EXTRACTOR : ClassProvider = {
    provide: StepTransformer,
    useClass: HintCategoryExtractor,
    multi: true
};



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/pipeline/hint-category-extractor.ts